import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Download, Users } from 'lucide-react';
import axios from 'axios';
import AdminHeader from '../components/AdminHeader';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

function formatPhone(phone) {
  if (!phone) return '—';
  const n = String(phone).replace(/\D/g, '');
  if (n.length === 9) return `${n.slice(0,3)} ${n.slice(3,6)} ${n.slice(6)}`;
  return phone;
}

export default function AdminContacts() {
  const navigate = useNavigate();
  const token = localStorage.getItem('adminToken');
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  const fetchContacts = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/admin/contacts`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setContacts(res.data.data);
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem('adminToken');
        navigate('/backoffice');
      }
    } finally {
      setLoading(false);
    }
  }, [token, navigate]);

  useEffect(() => {
    if (!token) { navigate('/backoffice'); return; }
    fetchContacts();
  }, []);

  const handleExport = async () => {
    const res = await axios.get(`${API_URL}/admin/contacts/export`, {
      headers: { Authorization: `Bearer ${token}` },
      responseType: 'blob',
    });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement('a');
    a.href = url; a.download = 'contactos.csv'; a.click();
    URL.revokeObjectURL(url);
  };

  const filtered = contacts.filter(c => {
    const s = search.toLowerCase();
    return !s || c.email.toLowerCase().includes(s) || c.name.toLowerCase().includes(s) || (c.phone && c.phone.includes(s));
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <p className="text-gray-500">A carregar contactos...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <AdminHeader activePage="contacts" />

      <div className="container mx-auto px-4 py-6">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
          <div>
            <h2 className="text-lg font-bold text-gray-900">Contactos</h2>
            <p className="text-xs text-gray-400">{contacts.length} contacto{contacts.length !== 1 ? 's' : ''} registado{contacts.length !== 1 ? 's' : ''}</p>
          </div>
          <button
            onClick={handleExport}
            className="flex items-center gap-2 bg-purple-600 text-white px-4 py-2 rounded-xl font-semibold text-sm hover:bg-purple-700 transition"
          >
            <Download className="w-4 h-4" />
            Exportar CSV
          </button>
        </div>

        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 mb-4 p-4">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Pesquisar por nome, email ou telefone..."
              className="w-full pl-11 pr-4 py-2.5 border-2 border-gray-200 rounded-xl focus:border-purple-500 outline-none text-sm"
            />
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
          {filtered.length === 0 ? (
            <div className="py-16 text-center text-gray-400">
              <Users className="w-8 h-8 mx-auto mb-2 opacity-30" />
              <p className="text-sm">Nenhum contacto encontrado</p>
            </div>
          ) : (
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-100">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-bold text-gray-600">Nome</th>
                  <th className="px-4 py-3 text-left text-xs font-bold text-gray-600">Email</th>
                  <th className="px-4 py-3 text-left text-xs font-bold text-gray-600">Telefone</th>
                  <th className="px-4 py-3 text-center text-xs font-bold text-gray-600">Encomendas</th>
                  <th className="px-4 py-3 text-left text-xs font-bold text-gray-600">Primeiro Contacto</th>
                  <th className="px-4 py-3 text-left text-xs font-bold text-gray-600">Último Contacto</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {filtered.map(c => (
                  <tr key={c.id} className="hover:bg-gray-50 transition">
                    <td className="px-4 py-3 text-sm font-medium text-gray-900">{c.name}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{c.email}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{formatPhone(c.phone)}</td>
                    <td className="px-4 py-3 text-center">
                      <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-purple-100 text-purple-700 text-xs font-bold">
                        {c.ordersCount}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-500">{new Date(c.firstOrderAt).toLocaleDateString('pt-PT')}</td>
                    <td className="px-4 py-3 text-xs text-gray-500">{new Date(c.lastOrderAt).toLocaleDateString('pt-PT')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
